import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { Card, CardContent } from '@/components/ui/card';
import { Edit, Trash2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DayTemplate } from '@/types';
import { ConfirmationDialog } from '@/components/settings/ui/ConfirmationDialog';
import DayTemplatePreview from './shared/DayTemplatePreview';

interface DayTemplateItemProps {
  template: DayTemplate;
  compact?: boolean;
  enableDrag?: boolean;
  onEdit: (templateId: string) => void;
  onDelete: (templateId: string) => void;
  onSelect?: () => void;
}

// Removed width calculation constants as day templates now use flexible width

const DayTemplateItem: React.FC<DayTemplateItemProps> = ({
  template,
  compact = false,
  enableDrag = true,
  onEdit,
  onDelete,
  onSelect,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Add drag functionality for day templates (conditionally)
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'DAY_TEMPLATE',
    item: { templateId: template.id, template },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: () => enableDrag,
  }), [template.id, template, enableDrag]);

  // Calculate total duration for display purposes only (using extended 3-hour boundaries)
  const calculateTotalDuration = () => {
    if (template.shifts.length === 0) return 0;

    // Find the earliest start time and latest end time
    let actualEarliestMinutes = Infinity;
    let actualLatestMinutes = 0;

    template.shifts.forEach(shift => {
      const [startHour, startMinute] = shift.startTime.split(':').map(Number);
      const [endHour, endMinute] = shift.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      let endMinutes = endHour * 60 + endMinute;

      // Handle shifts that cross midnight
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60; // Add 24 hours
      }

      actualEarliestMinutes = Math.min(actualEarliestMinutes, startMinutes);
      actualLatestMinutes = Math.max(actualLatestMinutes, endMinutes);
    });

    // Extend to 3-hour boundaries for consistent timeline display
    const earliestMinutes = Math.floor(actualEarliestMinutes / 180) * 180;
    const latestMinutes = Math.ceil(actualLatestMinutes / 180) * 180;

    return latestMinutes - earliestMinutes;
  };

  const totalDuration = calculateTotalDuration();

  // Format duration in hours and minutes
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}m` : ''}`;
  };

  return (
    <div
      ref={enableDrag ? drag : undefined}
      className={`w-auto ${isDragging ? 'opacity-50' : 'opacity-100'} ${enableDrag ? 'cursor-move' : 'cursor-default'}`}
    >
      <Card className={`border border-gray-200 dark:border-gray-700 hover:border-primary transition-colors p-1`}>
        <CardContent className="p-0">
          {/* Header with title and buttons */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-1 mb-1">
                <h4 className={`font-medium text-gray-900 dark:text-gray-100 truncate text-xs`}>
                  {template.name}
                </h4>
              </div>
              <p className={`text-gray-500 dark:text-gray-400 text-xs`}>
                {template.shifts.length} shift{template.shifts.length !== 1 ? 's' : ''}
                {totalDuration > 0 && ` • ${formatDuration(totalDuration)}`}
              </p>
              {template.description && (
                <p className={`text-gray-400 dark:text-gray-500 truncate text-xs mt-1`}>
                  {template.description}
                </p>
              )}
            </div>
            <div className={`flex items-center gap-1 flex-shrink-0 ml-2 flex-col`}>
              <Edit
                className="h-4 w-4 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(template.id);
                }}
              />
              <ConfirmationDialog
                trigger={
                  <Trash2
                    className="h-4 w-4 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-red-600 dark:hover:text-red-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeleteDialogOpen(true);
                    }}
                  />
                }
                title="Delete Day Template"
                description={`Are you sure you want to delete "${template.name}"? This action cannot be undone.`}
                confirmText="Delete Template"
                cancelText="Cancel"
                onConfirm={() => {
                  onDelete(template.id);
                  setIsDeleteDialogOpen(false);
                }}
                onCancel={() => setIsDeleteDialogOpen(false)}
                variant="destructive"
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
              />
            </div>
          </div>

          {/* Full-width timeline preview */}
          <DayTemplatePreview shifts={template.shifts} />
        </CardContent>
      </Card>
    </div>
  );
};

export default DayTemplateItem;